/**
 * Page Audit Logs Admin
 * Affiche les logs d'audit avec filtres avancés et détails
 */

import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  Stack,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';

// Queries & Types
import { GET_AUDIT_LOGS, GET_AUDIT_STATS } from './audit.queries';
import {
  AuditLogEntry,
  AuditLogFilter,
  AuditStats,
  ACTION_LABELS,
  ACTION_COLORS,
  MODULE_LABELS,
  MODULE_COLORS,
  AuditAction,
  AuditModule,
} from './audit.types';

/**
 * Format date en français
 */
const formatDate = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(d);
};

/**
 * Stats Card Component
 */
interface StatsCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
}

const StatsCard: React.FC<StatsCardProps> = ({ title, value, icon, color }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          sx={{
            backgroundColor: `${color}15`,
            borderRadius: 2,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h5" fontWeight="bold">
            {value}
          </Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

/**
 * Detail Dialog Component
 */
interface DetailDialogProps {
  open: boolean;
  onClose: () => void;
  log: AuditLogEntry | null;
}

const DetailDialog: React.FC<DetailDialogProps> = ({ open, onClose, log }) => {
  if (!log) return null;

  const hasChanges = log.oldValue || log.newValue;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Détails de l'action - {ACTION_LABELS[log.action] || log.action}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Informations générales */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="bold">Informations générales</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Date/Heure
                  </Typography>
                  <Typography variant="body1">{formatDate(log.timestamp)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Utilisateur
                  </Typography>
                  <Typography variant="body1">
                    {log.userEmail || 'Système'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Rôle
                  </Typography>
                  <Typography variant="body1">{log.userRole || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Module
                  </Typography>
                  <Chip
                    label={MODULE_LABELS[log.module as AuditModule] || log.module}
                    color={MODULE_COLORS[log.module as AuditModule] || 'default'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Ressource
                  </Typography>
                  <Typography variant="body1">{log.resource}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    ID Ressource
                  </Typography>
                  <Typography variant="body1">{log.resourceId || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    IP
                  </Typography>
                  <Typography variant="body1">{log.userIp || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Statut
                  </Typography>
                  {log.success ? (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="Succès"
                      color="success"
                      size="small"
                    />
                  ) : (
                    <Chip
                      icon={<ErrorIcon />}
                      label="Erreur"
                      color="error"
                      size="small"
                    />
                  )}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Changements */}
          {hasChanges && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="bold">Changements</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {log.oldValue && (
                    <Grid item xs={12} md={6}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          backgroundColor: '#FEE2E2',
                          borderColor: '#EF4444',
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color="error"
                          gutterBottom
                        >
                          Ancienne valeur
                        </Typography>
                        <pre
                          style={{
                            fontSize: '0.75rem',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            margin: 0,
                          }}
                        >
                          {JSON.stringify(log.oldValue, null, 2)}
                        </pre>
                      </Paper>
                    </Grid>
                  )}
                  {log.newValue && (
                    <Grid item xs={12} md={6}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          backgroundColor: '#D1FAE5',
                          borderColor: '#10B981',
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color="success.main"
                          gutterBottom
                        >
                          Nouvelle valeur
                        </Typography>
                        <pre
                          style={{
                            fontSize: '0.75rem',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            margin: 0,
                          }}
                        >
                          {JSON.stringify(log.newValue, null, 2)}
                        </pre>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Contexte technique */}
          {(log.endpoint || log.method || log.metadata) && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="bold">Contexte technique</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {log.endpoint && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Endpoint
                      </Typography>
                      <Typography variant="body1">{log.endpoint}</Typography>
                    </Grid>
                  )}
                  {log.method && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Méthode HTTP
                      </Typography>
                      <Chip label={log.method} size="small" />
                    </Grid>
                  )}
                  {log.metadata && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Métadonnées
                      </Typography>
                      <pre
                        style={{
                          fontSize: '0.75rem',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          margin: 0,
                          backgroundColor: '#F3F4F6',
                          padding: '8px',
                          borderRadius: '4px',
                        }}
                      >
                        {JSON.stringify(log.metadata, null, 2)}
                      </pre>
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Erreur */}
          {!log.success && log.errorMessage && (
            <Alert severity="error">
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Message d'erreur
              </Typography>
              <Typography variant="body2">{log.errorMessage}</Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Main Component - Audit Logs Page
 */
export const AuditLogsPage: React.FC = () => {
  // State
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLogEntry | null>(null);
  const [filter, setFilter] = useState<AuditLogFilter>({});

  // Queries
  const {
    data: logsData,
    loading: logsLoading,
    error: logsError,
  } = useQuery(GET_AUDIT_LOGS, {
    variables: {
      filter,
      page: page + 1,
      pageSize,
    },
    fetchPolicy: 'network-only',
  });

  const { data: statsData, loading: statsLoading } = useQuery(GET_AUDIT_STATS, {
    fetchPolicy: 'cache-and-network',
  });

  // Handlers
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (key: keyof AuditLogFilter, value: any) => {
    setFilter((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const handleResetFilters = () => {
    setFilter({});
    setPage(0);
  };

  // Data
  const logs = logsData?.adminAuditLogs?.items || [];
  const total = logsData?.adminAuditLogs?.total || 0;
  const stats: AuditStats | undefined = statsData?.adminAuditStats;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Logs d'audit
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Historique complet des actions effectuées sur la plateforme
        </Typography>
      </Box>

      {/* Stats Cards */}
      {!statsLoading && stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Total actions"
              value={stats.totalActions.toLocaleString('fr-FR')}
              icon={<HistoryIcon sx={{ color: '#3B82F6', fontSize: 28 }} />}
              color="#3B82F6"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Erreurs"
              value={stats.errorsCount.toLocaleString('fr-FR')}
              icon={<ErrorIcon sx={{ color: '#EF4444', fontSize: 28 }} />}
              color="#EF4444"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Module principal"
              value={MODULE_LABELS[stats.topModule as AuditModule] || stats.topModule}
              icon={<TrendingUpIcon sx={{ color: '#10B981', fontSize: 28 }} />}
              color="#10B981"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Aujourd'hui"
              value={stats.todayCount.toLocaleString('fr-FR')}
              icon={<CheckCircleIcon sx={{ color: '#D4AF37', fontSize: 28 }} />}
              color="#D4AF37"
            />
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: showFilters ? 2 : 0 }}>
          <Button
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters(!showFilters)}
            variant={showFilters ? 'contained' : 'outlined'}
          >
            Filtres {Object.keys(filter).length > 0 && `(${Object.keys(filter).length})`}
          </Button>
          {Object.keys(filter).length > 0 && (
            <Button onClick={handleResetFilters} color="error">
              Réinitialiser
            </Button>
          )}
        </Box>

        {showFilters && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Action</InputLabel>
                <Select
                  value={filter.action || ''}
                  label="Action"
                  onChange={(e) => handleFilterChange('action', e.target.value || undefined)}
                >
                  <MenuItem value="">Toutes</MenuItem>
                  {Object.keys(ACTION_LABELS).map((action) => (
                    <MenuItem key={action} value={action}>
                      {ACTION_LABELS[action as AuditAction]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Module</InputLabel>
                <Select
                  value={filter.module || ''}
                  label="Module"
                  onChange={(e) => handleFilterChange('module', e.target.value || undefined)}
                >
                  <MenuItem value="">Tous</MenuItem>
                  {Object.keys(MODULE_LABELS).map((module) => (
                    <MenuItem key={module} value={module}>
                      {MODULE_LABELS[module as AuditModule]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Ressource"
                value={filter.resource || ''}
                onChange={(e) => handleFilterChange('resource', e.target.value || undefined)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Utilisateur (email)"
                value={filter.userId || ''}
                onChange={(e) => handleFilterChange('userId', e.target.value || undefined)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Statut</InputLabel>
                <Select
                  value={filter.success === undefined ? '' : filter.success.toString()}
                  label="Statut"
                  onChange={(e) =>
                    handleFilterChange(
                      'success',
                      e.target.value === '' ? undefined : e.target.value === 'true',
                    )
                  }
                >
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="true">Succès</MenuItem>
                  <MenuItem value="false">Erreur</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Error Alert */}
      {logsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Erreur lors du chargement des logs: {logsError.message}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date/Heure</TableCell>
              <TableCell>Utilisateur</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Ressource</TableCell>
              <TableCell>Module</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logsLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Aucun log trouvé</Typography>
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log: AuditLogEntry) => (
                <TableRow key={log.id} hover>
                  <TableCell>{formatDate(log.timestamp)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{log.userEmail || 'Système'}</Typography>
                    {log.userIp && (
                      <Typography variant="caption" color="text.secondary">
                        {log.userIp}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ACTION_LABELS[log.action as AuditAction] || log.action}
                      color={ACTION_COLORS[log.action as AuditAction] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{log.resource}</Typography>
                    {log.resourceLabel && (
                      <Typography variant="caption" color="text.secondary">
                        {log.resourceLabel}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={MODULE_LABELS[log.module as AuditModule] || log.module}
                      color={MODULE_COLORS[log.module as AuditModule] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {log.success ? (
                      <CheckCircleIcon sx={{ color: '#10B981', fontSize: 20 }} />
                    ) : (
                      <ErrorIcon sx={{ color: '#EF4444', fontSize: 20 }} />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => setSelectedLog(log)}
                      color="primary"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={pageSize}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Lignes par page"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`
          }
        />
      </TableContainer>

      {/* Detail Dialog */}
      <DetailDialog
        open={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        log={selectedLog}
      />
    </Box>
  );
};

export default AuditLogsPage;
